// -----------------------------------------------------------------------------
// src/components/AppShell.jsx   — 2025-06-30 • chat-aware bottom nav
// -----------------------------------------------------------------------------
import React, {
  useState, useEffect, useRef, useCallback, useMemo,
} from "react";
import {
  Box, Paper, IconButton, Menu, MenuItem, Tooltip, useMediaQuery, Badge,
} from "@mui/material";
import IosShareIcon                from "@mui/icons-material/IosShare";
import MenuIcon                    from "@mui/icons-material/Menu";
import HomeOutlined                from "@mui/icons-material/HomeOutlined";
import ChatBubbleOutlineOutlined   from "@mui/icons-material/ChatBubbleOutlineOutlined";
import StorefrontOutlined          from "@mui/icons-material/StorefrontOutlined";
import HandshakeOutlined           from "@mui/icons-material/HandshakeOutlined";
import InsertDriveFileOutlined     from "@mui/icons-material/InsertDriveFileOutlined";
import PeopleOutline               from "@mui/icons-material/PeopleOutline";
import SupervisorAccountOutlined   from "@mui/icons-material/SupervisorAccountOutlined";
import AttachMoneyIcon             from "@mui/icons-material/AttachMoney";
import AccountCircleIcon           from "@mui/icons-material/AccountCircle";
import AppsIcon                    from "@mui/icons-material/Apps";
import DashboardIcon               from "@mui/icons-material/Dashboard";
import { Share }                   from "@capacitor/share";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useQuery }                from "react-query";

import routes            from "./Config/routes";
import useAuth           from "./Hooks/use-auth";
import { useCreatorAuth } from "./Hooks/creator-use-auth";
import client            from "./API";

/*────────────────────────────────────────────────────────────────────────────*/
const WEB_BASE      = "https://www.useblitz.co";
const HIDE_AFTER    = 5000;      // ms until bar fades
const BAR_HEIGHT    = 86;        // px – 78px + 10% height boost
const ICON_SIZE     = "large";
const PAY_SIZE      = 60;        // px – primary FAB (Pay button)
const MAX_MAIN_BTNS = 4;         // Home/Dash, Chat, Pay, +1 extra

/*────────────────────────────────────────────────────────────────────────────*/
export default function AppShell() {
  const navigate   = useNavigate();
  const location   = useLocation();
  const pathname   = location.pathname;
  const search     = location.search;

  const isMobile   = useMediaQuery((t) => t.breakpoints.down("md"));
  const isNative   = Boolean(window.Capacitor?.isNativePlatform?.());

  /*──────── auth */
  const { isAuthorized, getCurrrentUser } = useAuth();
  const user                 = getCurrrentUser?.();
  const { creatorToken }     = useCreatorAuth();
  const isCreatorAuthorized  = Boolean(creatorToken) && !isAuthorized;

  /*──────── fade timer */
  const [visible, setVisible] = useState(true);
  const timerRef = useRef();
  const resetTimer = useCallback(() => {
    setVisible(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setVisible(false), HIDE_AFTER);
  }, []);
  useEffect(() => {
    resetTimer();
    const events = ["touchstart", "mousedown", "keydown", "scroll"];
    events.forEach((e) => window.addEventListener(e, resetTimer, { passive: true }));
    return () => {
      events.forEach((e) => window.removeEventListener(e, resetTimer));
      clearTimeout(timerRef.current);
    };
  }, [resetTimer]);

  /*──────── share */
  const handleShare = async () => {
    const canonical = `${WEB_BASE}${location.pathname}${location.search}${location.hash}`;
    try {
      await Share.share({ title: document.title || "Share", url: canonical });
    } catch (err) { console.error("Share failed:", err); }
  };

  /*──────── queries (unread counts) */
  const { data: dashboardData = {} } = useQuery(
    ["userDashboard"],
    () => client.users.dashboard(),
    { enabled: isAuthorized, staleTime: 300_000 },
  );
  const unreadConversationCount = dashboardData.unread_conversations_count ?? 0;

  const { data: unreadCollabData = {} } = useQuery(
    ["notifications", "unread_collab_count"],
    () => client.notifications.getUnreadCollabCount(),
    { enabled: isAuthorized, staleTime: 300_000 },
  );
  const unreadCollabCount = unreadCollabData.unread_collab_count ?? 0;

  const creatorUsername = creatorToken?.creator_user?.username;
  const { data: creatorConvos = [] } = useQuery(
    ["creatorConversations", creatorUsername],
    () => client.conversations.fetchCreatorConversations(creatorUsername),
    { enabled: isCreatorAuthorized },
  );
  const unreadCreatorChatCount = useMemo(
    () => creatorConvos.reduce((a, c) => a + (Number(c.unread_count) || 0), 0),
    [creatorConvos],
  );

  /*──────── nav button sets */
  const userBtns = [
    { k: "home",  r: routes.dashboard,               i: <HomeOutlined /> },
    { k: "chat",  r: routes.conversationshub,        i: <Badge badgeContent={unreadConversationCount} color="secondary" max={99} invisible={!unreadConversationCount}><ChatBubbleOutlineOutlined /></Badge> },
    { k: "pay",   r: routes.blitzpay,                i: <AttachMoneyIcon fontSize="inherit" />, big: true },
    { k: "search",r: routes.search,                  i: <StorefrontOutlined /> },
    { k: "collab",r: routes.collaborations,          i: <Badge badgeContent={unreadCollabCount} color="secondary" max={99} invisible={!unreadCollabCount}><HandshakeOutlined /></Badge> },
  ];

  const creatorBtns = [
    { k: "dash",  r: routes.creatorConnectStart,                i: <DashboardIcon /> },
    { k: "chat",  r: routes.creatorConvos,                       i: <Badge badgeContent={unreadCreatorChatCount} color="secondary" max={99} invisible={!unreadCreatorChatCount}><ChatBubbleOutlineOutlined /></Badge> },
    { k: "pay",   r: routes.creatorConnectPayoutsInvoicing,      i: <AttachMoneyIcon fontSize="inherit" />, big: true },
    { k: "bio",   r: routes.creatorbio,                          i: <AccountCircleIcon /> },
  ];

  const btns         = isAuthorized ? userBtns : isCreatorAuthorized ? creatorBtns : [];
  const mainBtns     = btns.slice(0, MAX_MAIN_BTNS);
  const overflowBtns = btns.slice(MAX_MAIN_BTNS);

  /*──────── overflow menu items */
  const staticMenuItems = useMemo(() => {
    const items = [];
    if (isAuthorized) {
      items.push({ t: "Files", r: routes.files, i: <InsertDriveFileOutlined /> });
      if (user?.account_status === "Agency" || user?.account_status === "AlphaFree") {
        items.push({ t: "My Agency", r: routes.agency, i: <PeopleOutline /> });
      }
      if (user?.type === "admin") {
        items.push({ t: "Admin", r: routes.company, i: <SupervisorAccountOutlined /> });
      }
    } else if (isCreatorAuthorized) {
      items.push({ t: "App Store", r: routes.creatorConnectApps, i: <AppsIcon /> });
    }
    return items;
  }, [isAuthorized, isCreatorAuthorized, user]);

  const extraMenuItems = overflowBtns.map(({ k, r, i }) => ({
    t: k.charAt(0).toUpperCase() + k.slice(1), r, i,
  }));
  const menuItems = [...extraMenuItems, ...staticMenuItems];

  const [anchorEl, setAnchorEl] = useState(null);
  const safeBottom = "env(safe-area-inset-bottom,0px)";

  /*──────── nav-visibility helpers */
  const qs = useMemo(() => new URLSearchParams(search), [search]);

  const isChatScreen = useMemo(() => (
    pathname.includes("/convo/") ||
    pathname.includes("/join/")  ||
    qs.has("conversation")       ||
    qs.has("with")
  ), [pathname, qs]);

  const showBottomNav = useMemo(() => {
    if (isChatScreen) return false;
    if (isMobile) return true;
    /* desktop hubs */
    return pathname.startsWith("/convos/") || pathname.startsWith("/creatorconnect/convos/");
  }, [isChatScreen, isMobile, isNative, pathname]);

  /*───────────────── render ─────────────────*/
  return (
    <Box sx={{ pb: safeBottom, minHeight: "100vh", width: "100%", display: "flex", flexDirection: "column", overflowX: "hidden" }}>
      {/* main routed content */}
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", pb: showBottomNav ? `${BAR_HEIGHT}px` : 0 }}>
        <Outlet />
      </Box>

      {showBottomNav && (
        <Paper
          elevation={8}
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            height: BAR_HEIGHT,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            bgcolor: "background.paper",
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            transition: "opacity 250ms",
            opacity: visible ? 0.98 : 0,
            pointerEvents: visible ? "auto" : "none",
            zIndex: (theme) => theme.zIndex.modal + 2,
          }}
        >
          {/* central nav & overflow menu */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2, flex: 1 }}>
            {mainBtns.map(({ k, i, r, big }) => (
              <Tooltip key={k} title={k.charAt(0).toUpperCase() + k.slice(1)}>
                <IconButton
                  size={ICON_SIZE}
                  onClick={() => navigate(r)}
                  sx={big ? {
                    width: PAY_SIZE,
                    height: PAY_SIZE,
                    borderRadius: "50%",
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    boxShadow: 4,
                    "&:hover": { bgcolor: "primary.dark" },
                  } : undefined}
                >
                  {i}
                </IconButton>
              </Tooltip>
            ))}

            {menuItems.length > 0 && (
              <>
                <Tooltip title="Menu">
                  <IconButton size={ICON_SIZE} onClick={(e) => setAnchorEl(e.currentTarget)}>
                    <MenuIcon />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={() => setAnchorEl(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                  transformOrigin={{ vertical: "bottom", horizontal: "center" }}
                >
                  {menuItems.map(({ t, r, i }) => (
                    <MenuItem
                      key={t}
                      onClick={() => {
                        setAnchorEl(null);
                        navigate(r);
                      }}
                    >
                      <Box sx={{ mr: 1 }}>{i}</Box> {t}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}
          </Box>

          {/* share button */}
          <Tooltip title="Share">
            <IconButton size={ICON_SIZE} onClick={handleShare}>
              <IosShareIcon />
            </IconButton>
          </Tooltip>
        </Paper>
      )}
    </Box>
  );
}
